import { FormControl } from "@ecco/ui/form-control";
import { FormErrorMessage } from "@ecco/ui/form-error";
import { FormLabel } from "@ecco/ui/form-label";
import { Input } from "@ecco/ui/input";
import { Text } from "@ecco/ui/text";
import { Field } from "formik";
import { styles } from "./baseFormStyles";
import type { SingleFieldProps } from "./baseFormsValidations";
import { InputGroup } from "@ecco/ui/input-group";
import { InputRightElement } from "@ecco/ui/input-element";
import { useId } from "react";
import { ECCOIcon } from "@ecco/ui/ecco-icon";

export const BaseInputField = ({
    error,
    isInvalid,
    isSuccess,
    header,
    id,
    name,
    required = true,
    isReadOnly = false,
    rightIcon,
    onBlur,
    onChange,
    validate,
    ariaLabel,
    ariaLabelledBy,
    testId,
    helpText,
    helpTextColor = "black",
    type = "text",
    autocomplete,
    inputMode,
    tabIndex,
    displayValue,
    placeholder,
}: SingleFieldProps) => {
    const dynamicId = useId();
    const htmlId = id ?? dynamicId;
    const htmlName = name ?? htmlId;
    const iconProps = {
        rightIcon,
        isInvalid,
        isSuccess,
        ariaLabel,
    };

    return (
        <FormControl
            isInvalid={isInvalid}
            isRequired={required}
            isReadOnly={isReadOnly}
            isSuccess={isSuccess}
        >
            {header && (
                <FormLabel htmlFor={htmlId} sx={styles.formLabel}>
                    {header}
                </FormLabel>
            )}
            <InputGroup>
                <Field
                    data-testid={testId}
                    as={Input}
                    id={htmlId}
                    name={htmlName}
                    type={type}
                    sx={
                        rightIcon
                            ? { ...styles.formInput, ...styles.formInputWithRightIcon }
                            : styles.formInput
                    }
                    autoComplete={autocomplete}
                    inputMode={inputMode}
                    tabIndex={tabIndex}
                    {...(placeholder && { placeholder })}
                    // Formik spreads its own value first, so this wins where it is given.
                    {...(displayValue !== undefined && { value: displayValue })}
                    aria-labelledby={ariaLabelledBy}
                    {...(validate && { validate })}
                    // if onBlur or onChange are passed as undefined it will break Formik
                    {...(onBlur && { onBlur })}
                    {...(onChange && { onChange })}
                />
                {rightIcon && (
                    <InputRightElement h={"100%"}>
                        <FieldIcon {...iconProps} />
                    </InputRightElement>
                )}
            </InputGroup>
            {helpText && !(isInvalid && error) && (
                <Text fontSize={"mobileBodyB4"} pt={2} size={"tiny"} color={helpTextColor}>
                    {helpText}
                </Text>
            )}
            <FormErrorMessage>{error}</FormErrorMessage>
        </FormControl>
    );
};

export const FieldIcon = ({
    rightIcon,
    isInvalid,
    isSuccess,
    ariaLabel,
}: Pick<SingleFieldProps, "rightIcon" | "isInvalid" | "isSuccess" | "ariaLabel">) => {
    const getIconStyles = () => {
        if (isSuccess) {
            return styles.icon.success;
        }
        if (isInvalid) {
            return styles.icon.invalid;
        }
        return {};
    };

    if (typeof rightIcon === "string") {
        return (
            <ECCOIcon
                name={rightIcon}
                size="sm"
                aria-label={ariaLabel}
                aria-hidden={!ariaLabel}
                sx={{ ...getIconStyles() }}
            />
        );
    }

    return rightIcon;
};

export default BaseInputField;
